import { useEffect, useLayoutEffect, useRef, useState } from "react";
import LoaderItem from "./LoaderItem";
import AccordionItem from "./AccordionItem";
import CellAccordion from "./CellAccordion";
import { isBuildingTrial } from "@utils/checkPermiossion";
import { checkIfMultipleIds, unitsIntoArray } from "@utils/checkMultipleIds";
import EscapeHandler from "@utils/escapeHandler";
import { downloadCsv } from "@utils/downloadCsv";
import { trackReportDownload } from "@utils/activityTracker";
import Modal from "@components/Modal";
import { loadStorage, saveStorage } from "@utils/localStorageManager";
import {
  normalizeValueNodePart,
  ValueHierarchyProvider,
} from "./ValueHierarchyContext";
import ValueHierarchyBreadcrumb from "./ValueHierarchyBreadcrumb";

const { useProjectContext, useProjectActions } = require("@contexts/projects")

const fields = [
  { key: "wbs_code_id", label: "WBS" },
  { key: "building_unit_name", label: "Nome" },
  { key: "level", label: "Nível" },
  { key: "budget", label: "Orçado" },
  { key: "indexed_budget", label: "Orçado Indexado" },
  { key: "predicted", label: "Previsto" },
  { key: "measured", label: "Medido" },
  { key: "measurement_date", label: "Data de medição" },
  { key: "measurement_number", label: "Número da medição" },
  { key: "paid", label: "Pago" },
  { key: "toPay", label: "A Pagar" },
  { key: "commited", label: "Comprometido" },
  { key: "accumulated", label: "Realizado Acumulado" },
  { key: "idc", label: "IDC" },
  { key: "trend", label: "Tendência" },
  { key: "stockValue", label: "Estoque Total" },
  { key: "stockValueAmpli", label: "Estoque Ampli" },
  { key: "stockValueApi", label: "Estoque Sienge" },
  { key: "stockTrend", label: "Tendência com Estoque" },
  { key: "stockIdc", label: "IDC com Estoque" },
  { key: "disbursement", label: "Desembolso" },
  { key: "totalPhysical", label: "Físico Financeiro" },
  { key: "paidCub", label: "Pago/CUB" },
  { key: "toPayCub", label: "A Pagar/Cub" },
  { key: "extraValue.value", label: "Estouro / Economia" },
  { key: "budget_minus_paid", label: "Orçado menos Pago" },
  { key: "indexed_balance", label: "Saldo Indexado" },
]

const RenderCostCenter = ({ units, filters }) => {
  const [isOpen, setIsOpen] = useState(true)
  const toggleRef = useRef(null);
  const costCenterLabel = units[0]?.wbs_code_id_building || "Centro de custo";
  const costCenterId = `value-cost-center-${normalizeValueNodePart(costCenterLabel)}`;
  const closeCostCenter = () => {
    setIsOpen(false);
    toggleRef.current?.focus();
  };
  const onCostCenterKeyDown = (event) => {
    if (event.key !== "Escape" || !isOpen) return;
    event.preventDefault();
    event.stopPropagation();
    closeCostCenter();
  };

  return <>
    {checkIfMultipleIds() && <>
      <div className="wbs-accordion-divider flex gap-4">
        <button
          ref={toggleRef}
          type="button"
          className={`accordion-toggle ${isOpen ? "active" : ""}`}
          onClick={() => setIsOpen(!isOpen)}
          onKeyDown={onCostCenterKeyDown}
          aria-label={`${isOpen ? "Recolher" : "Expandir"} ${costCenterLabel}`}
          aria-expanded={isOpen}
          aria-controls={costCenterId}
        />
        <h2>{costCenterLabel}</h2>
      </div>
      <div className="hr-simulator" />
    </>}
    <div id={costCenterId} hidden={!isOpen}>
      {isOpen && units.map((item) => <AccordionItem
        key={item.wbs_code_id || item.building_unit_id}
        item={item}
        type={"unit"}
        typeTitle={"Unidade construtiva"}
        ChildAccordionComponent={
          item.building_unit_name === 'ITENS SEM APROPRIAÇÃO' ? null : CellAccordion
        }
        filters={filters}
        path={[]}
      />)}
    </div>
    {/* <div className="hr-simulator" /> */}
    {/* <AccordionItem
      item={units.reduce((acc, unit) => {
        Object.keys(unit).forEach(key => {
          if (typeof unit[key] === "number") {
            acc[key] = (acc[key] || 0) + unit[key];
          }
        });
        return acc;
      }, {})}
      type={"totals"}
      typeTitle={"Valores Totais"}
      ChildAccordionComponent={null}
      filters={filters} /> */}
  </>

}

const UnitAccordionContent = () => {
  const [filters, setFilters] = useState({
    first: 'budget',
    second: "commited",
    third: "idc",
    fourth: 'paid'
  })
  const { activeProject, filterDates } = useProjectContext();
  const { _id, units, wbs_code_ids } = activeProject
  const idsLength = wbs_code_ids.length
  const { loadUnits, loadCells, loadSteps, loadSubsteps, loadServices, getItemsCount } = useProjectActions()
  const [modalVisible, setModalVisible] = useState(false)
  const [totalItems, setTotalItems] = useState(0)
  const [currentItem, setCurrentItem] = useState('')
  const [downloadStatus, setDownloadStatus] = useState('notStarted')
  const [totalItemsExtracted, setTotalItemsExtracted] = useState(0)
  const exportRunRef = useRef(0)
  const projectionBeginAt = filterDates?.beginAt?.getTime?.()
  const projectionEndAt = filterDates?.endAt?.getTime?.()

  useLayoutEffect(() => {
    exportRunRef.current += 1
    return () => {
      exportRunRef.current += 1
    }
  }, [_id, projectionBeginAt, projectionEndAt])

  useEffect(() => {
    if (_id) {
      loadUnits();
      const storedFilters = loadStorage((`${_id}_value_filters`))
      if (storedFilters) {
        setFilters(storedFilters)
      }
    }
  }, [_id])

  const handleFilterChange = (e) => {
    const { name, value } = e.target
    setFilters(prev => ({
      ...prev,
      [name]: value
    }))
    saveStorage(`${_id}_value_filters`, {
      ...filters,
      [name]: value
    })
  }

  const cancelExport = () => {
    exportRunRef.current += 1
    setModalVisible(false)
  }

  const openModal = async () => {
    const res = await getItemsCount()
    setTotalItems(res.total)
    setModalVisible(true)
  }

  const test = []

  const downloadAll = async (e) => {
    const runToken = exportRunRef.current + 1
    exportRunRef.current = runToken
    const isCurrentRun = () => exportRunRef.current === runToken
    setDownloadStatus('started')
    setTotalItemsExtracted(prev => prev + 1)
    const startTime = performance.now()
    const units = await loadUnits()
    if (!isCurrentRun()) return

    for (const i of units) {
      test.push({ ...i, level: 'Unidade Construtiva' })
      setCurrentItem(i.building_unit_name)
      setTotalItemsExtracted(prev => prev + 1)
      const cells = await loadCells(i.wbs_code_id)
      if (!isCurrentRun()) return

      for (const j of cells) {
        test.push({ ...j, level: 'Célula' })
        setCurrentItem(j.description)
        setTotalItemsExtracted(prev => prev + 1)
        const steps = await loadSteps(j.wbs_code_id)
        if (!isCurrentRun()) return

        for (const k of steps) {
          test.push({ ...k, level: 'Etapa' })
          setCurrentItem(k.description)
          setTotalItemsExtracted(prev => prev + 1)
          const substeps = await loadSubsteps(k.wbs_code_id)
          if (!isCurrentRun()) return

          for (const l of substeps) {
            test.push({ ...l, level: 'Subetapa' })
            setCurrentItem(l.description)
            setTotalItemsExtracted(prev => prev + 1)
            const services = await loadServices(l.wbs_code_id)
            if (!isCurrentRun()) return

            for (const m of services) {
              test.push({ ...m, level: 'Serviço' })
              setCurrentItem(m.description)
              setTotalItemsExtracted(prev => prev + 1)
            }
          }
        }
      }

    }

    if (!isCurrentRun()) return
    downloadCsv(e, fields, test)
    setDownloadStatus('finished')

    const endTime = performance.now()
    trackReportDownload("valor_agregado_completo", {
      format: "xlsx",
      items: test.length,
      seconds: Math.round((endTime - startTime) / 1000),
    })
    console.log(`Took ${(endTime - startTime) / 1000} seconds to extract ${test.length} items`)
  }

  return (
    <div className="accordion value-accordion-tree margin-top-24">
      <EscapeHandler func={cancelExport} />
      {modalVisible && <Modal close={cancelExport}>
        <h3 className="update-title">Extrair relatório completo</h3>
        <div className="close-icon" onClick={cancelExport}>
          <i
            className="icon icon-width-24 icon-circle-x background-accent"
            aria-hidden={true}
          />
        </div>
        <p className="update-confirm" style={{ maxWidth: '600px' }}>A extração completa pode levar algum minutos para ser concluída,
          por favor não feche ou atualize a página até que o processo seja concluído.</p>
        {downloadStatus === 'started' && <>
          <p>Extraindo {currentItem}</p>
          <p>{totalItemsExtracted}/{totalItems}</p>
          <div className="loading-bar-container" style={{ width: '100%', backgroundColor: '#e0e0e0', borderRadius: '8px', overflow: 'hidden', marginTop: '16px' }}>
            <div
              className="loading-bar"
              style={{
                width: `${(totalItemsExtracted / totalItems) * 100}%`,
                backgroundColor: '#4caf50',
                height: '8px',
                transition: 'width 0.3s ease'
              }}
            />
          </div>
        </>}

        {downloadStatus === 'notStarted' && <p className="update-confirm">Total de itens: {totalItems}</p>}
        {downloadStatus === 'finished' && <>
          <p className="update-confirm" style={{ fontWeight: 'bold', color: "green" }}>Extração concluída!</p>
          <p style={{ color: "blue", textDecoration: 'underline', cursor: 'pointer' }} onClick={(e) => downloadCsv(e, fields, test)}>Caso o download não comece automaticamente, clique aqui.</p>
        </>}
        <div className="flex gap-16 margin-top-24" style={{ justifyContent: 'space-around' }}>
          <button className="button button-error" onClick={cancelExport}>Cancelar</button>
          <button className="button button-accent" onClick={downloadAll}>Extrair Relatório Completo</button>
        </div>
      </Modal>}
      {!isBuildingTrial() && <div className="accordion-selectors">
        <button className="button button-accent small" onClick={openModal}>Extrair relatório</button>
        <label className="value-filter">
          <span>Orçamento</span>
          <select name="first" value={filters.first} onChange={handleFilterChange}>
            <option value="budget">Orçado</option>
            <option value="indexed_budget">Orçado Indexado</option>
          </select>
        </label>
        <label className="value-filter">
          <span>Execução</span>
          <select name="fourth" value={filters.fourth} onChange={handleFilterChange}>
            <option value="paid">Pago</option>
            <option value="paidStock">Pago - Estoque</option>
            <option value="accumulated">Realizado</option>
            <option value="accumulatedStock">Realizado - Estoque</option>
          </select>
        </label>
        <label className="value-filter">
          <span>Compromissos</span>
          <select name="second" value={filters.second} onChange={handleFilterChange}>
            <option value="commited">Comprometido</option>
            <option value="extraValue">Estouro / Economia</option>
            <option value="commited_extra">Comprometido + Estouros / Economias</option>
            <option value="stockValueAmpli">Estoque (Ampli)</option>
            <option value="stockValueApi">Estoque (Sienge)</option>
            <option value="stockValue">Estoque (Total)</option>
            <option value="budget_minus_paid">Saldo</option>
            <option value="indexed_balance">Saldo Indexado</option>
          </select>
        </label>
        <label className="value-filter">
          <span>Indicador</span>
          <select name="third" value={filters.third} onChange={handleFilterChange}>
            <option value="idc">IDC</option>
            <option value="stockIdc">IDC com Estoque</option>
            <option value="trend">Tendência</option>
            <option value="stockTrend">Tendência com Estoque</option>
            <option value="trendExtra">Tendência + Estouros / Economias</option>
          </select>
        </label>
      </div>}

      <ValueHierarchyBreadcrumb />

      {!units && (
        <LoaderItem />
      )}

      {units && unitsIntoArray().map(i => {
        return <RenderCostCenter
          key={i[0]?.wbs_code_id_building || i[0]?.wbs_code_id || i[0]?.building_unit_id}
          units={i}
          filters={filters}
        />
      })}

    </div>
  )
}

const UnitAccordion = () => {
  const { activeProject, filterDates } = useProjectContext();
  const hierarchyScope = [
    activeProject?._id,
    filterDates?.beginAt?.toISOString?.(),
    filterDates?.endAt?.toISOString?.(),
  ].join(":");

  return (
    <ValueHierarchyProvider scopeKey={hierarchyScope}>
      <UnitAccordionContent />
    </ValueHierarchyProvider>
  );
};

export default UnitAccordion;
